import {
  Component,
  OnInit,
  ViewEncapsulation,
  Renderer2,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import StrapiService from 'src/app/services/strapi.service';
import { GenericApiResponse } from 'src/app/interfaces/api';
import { Footer, IStrapiDocument } from 'src/app/interfaces/strapi';

import { NgIf, NgFor } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import {
  ICheckIsUnihomeResponse,
  UniService,
} from '../../../services/uni.service';
import { CmsLinkComponent } from '../cms-link/cms-link.component';
import { RenderMarkdownComponent } from '../render-markdown/render-markdown.component';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgIf,
        RouterLink,
        NgFor,
        CmsLinkComponent,
        RenderMarkdownComponent,
    ],
})
export default class FooterComponent implements OnInit, OnDestroy {
  public footerData: Footer | undefined;

  public isEISPage: boolean = false;

  // Default to true to prevent flashing of banner
  public isUniHomes: boolean | null | undefined = true;

  private userSubscription: Subscription | null = null;

  private routerSubscription: Subscription | null = null;

  constructor(
    public readonly auth: AuthService,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly strapi: StrapiService,
    private readonly uniService: UniService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Defer the initial footer data fetch
    this.strapi.getFooter().subscribe((data: IStrapiDocument<Footer>) => {
      Promise.resolve().then(() => {
        this.parseFooterData(data.data.attributes);
        this.cdr.detectChanges();
      });
    });

    // Initialize isUniHomes based on current user state
    this.deferIsUniHomesUpdate(
      this.auth.currentUser?.university?.is_unihome_product ?? false
    );

    // Subscribe to user changes
    this.userSubscription = this.auth.getCurrentUser().subscribe((user) => {
      this.deferIsUniHomesUpdate(
        user?.university ? user.university.is_unihome_product : false
      );
    });

    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.deferUpdate(() => {
          this.isEISPage = event.url.includes('/eis/');
        });

        if (event.url.includes('universityUUID=')) {
          this.checkUrlForUnihome(event.url);
        } else if (localStorage.getItem('unihomesFlow')) {
          this.deferIsUniHomesUpdate(true);
        }
      });
  }

  private deferUpdate(fn: () => void): void {
    Promise.resolve().then(() => {
      fn();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    this.userSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  public toggleFooterLinkList(e: Event): void {
    if (e.target) {
      const parent = <HTMLElement>(<HTMLElement>e.target).parentNode;
      if (parent.classList.contains('footer-link-col-show')) {
        this.renderer.removeClass(parent, 'footer-link-col-show');
      } else {
        this.renderer.addClass(parent, 'footer-link-col-show');
      }
    }
  }


  private parseFooterData(page: Footer): void {
    this.footerData = page;
  }

  private checkUrlForUnihome(url: string) {
    // Get universityUUID paramater
    const universityUUID = url.split('universityUUID=')[1].split('&')[0];

    this.uniService.checkIsUnihomes(universityUUID).subscribe({
      next: (response: GenericApiResponse<ICheckIsUnihomeResponse>) => {
        this.deferIsUniHomesUpdate(response.data.is_unihome_product);
      },
    });
  }

  private deferIsUniHomesUpdate(value: boolean | null | undefined): void {
    Promise.resolve().then(() => {
      this.isUniHomes = value;
      this.cdr.detectChanges();
    });
  }
}
